import { Loader, SearchableSelect } from 'components';
import { useAuth } from 'contexts/auth';
import {
  EventHandler,
  FC,
  FormEventHandler,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  activateUser,
  deactivateUser,
  deleteUserByUsername,
  getAuthenticatedUser,
  getCustomers,
  getLocations,
  getUser,
  parseApiErrorMessage,
  updateUser
} from 'services';
import { Customer, Location, Role, UserProfile } from 'types';
import { mapById } from 'utils';
import { UserCustomers, UserLocations, UserTruckingCompanies } from '../new/user-associations';

import ErrorDialog from 'components/organization/dialog/error-dialog';
import { useResponse } from 'contexts/response';
import useConfirm from 'hooks/use-confirm';
import '../details/user-details.scss';

export const EditUser: FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { userProfile } = useAuth();
  const { handleError, setSuccess } = useResponse();
  const { getConfirmation, ConfirmationDialog } = useConfirm();

  const [user, setUser] = useState<Partial<UserProfile>>({});

  const username = useMemo(() => params.username, [params.username]);
  const role = useMemo(() => user.role ?? Role.NONE, [user.role]);
  const defaultLocation = useMemo(() => user?.defaultLocation, [user?.defaultLocation]);
  const customers = useMemo(() => mapById(user?.customers ?? []), [user?.customers]);
  const locations = useMemo(() => mapById(user?.locations ?? []), [user?.locations]);

  const [allCustomers, setAllCustomers] = useState<Map<number, Customer>>(new Map());
  const [allLocations, setAllLocations] = useState<Map<number, Location>>(new Map());

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    getLocations().then(mapById).then(setAllLocations);
    getCustomers().then(mapById).then(setAllCustomers);
  }, []);

  useEffect(() => {
    if (!!username) {
      if (username !== userProfile.username) {
        getUser(username).then(response => {
          if (response) {
            setUser(response);
          }
        });
      } else {
        getAuthenticatedUser().then(response => {
          if (response) {
            setUser(response);
          }
        });
      }
    }
  }, [username, setUser, userProfile.username]);

  const handleReset: FormEventHandler = useCallback(
    e => {
      e.preventDefault();
      if (!username || username === userProfile.username) {
        navigate('/settings', { replace: true });
      } else {
        navigate(`/settings/users`, { replace: true });
      }
    },
    [username, userProfile.username, navigate]
  );

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
      e.preventDefault();
      setIsSubmitting(true);

      if (
        !user ||
        !user.username ||
        !user.email ||
        !user.role ||
        !user.defaultLocation ||
        !user.customers ||
        !user.locations ||
        !user.truckingCompanies
      ) {
        handleError(
          'Missing data: ' +
            ['username', 'email', 'role', 'defaultLocation', 'customers', 'locations', 'truckingCompanies']
              .filter(key => !!user && !!user[key as keyof UserProfile])
              .join(', ')
        );
        setIsSubmitting(false);
        return;
      }

      const response = await updateUser({
        username: user.username,
        email: user.email,
        role: user.role,
        defaultLocation: user.defaultLocation,
        customers: user.customers,
        locations: user.locations,
        truckingCompanies: user.truckingCompanies
      });

      setIsSubmitting(false);
      if (response.ok) {
        setSuccess('User update successful');
      } else {
        const json: any = await response.json();
        handleError(`User update failed: ${json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText}`);
      }
      if (!username || username === userProfile.username) {
        navigate('/settings', { replace: true });
      } else {
        navigate(`/settings/users`, { replace: true });
      }
    },
    [user, username, userProfile.username, handleError, navigate, setSuccess]
  );

  const handleDeactivate: EventHandler<React.MouseEvent> = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!user.username) handleError('No username provided');
      else {
        e.preventDefault();
        setIsDeleting(true);

        const response = await deactivateUser(user.username);

        setIsSubmitting(false);
        if (response.ok) {
          setSuccess('User update successful');
        } else {
          const json: any = await response.json();
          handleError(`User update failed: ${json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText}`);
        }
        navigate(`/settings/users`, { replace: true });
      }
    },
    [user, username, userProfile.username, handleError, navigate, setSuccess]
  );

  const handleActivate: EventHandler<React.MouseEvent> = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!user.username) handleError('No username provided');
      else {
        e.preventDefault();
        setIsDeleting(true);

        const response = await activateUser(user.username);

        setIsSubmitting(false);
        if (response.ok) {
          setSuccess('User update successful');
        } else {
          const json: any = await response.json();
          handleError(`User update failed: ${json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText}`);
        }
        navigate(`/settings/users`, { replace: true });
      }
    },
    [user, username, userProfile.username, handleError, navigate, setSuccess]
  );

  const handleDeleteUser: EventHandler<React.MouseEvent> = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (!user.username) {
        handleError('No username provided');
        return;
      }
      const confirmed = await getConfirmation(
        'Delete user permanently?',
        `This cannot be undone. The account for ${user.username} (${user.email ?? ''}) will be permanently removed from the database and sign-in.`,
        'CANCEL',
        'DELETE'
      );
      if (confirmed !== 1) {
        return;
      }

      setIsDeleting(true);
      try {
        const response = await deleteUserByUsername(user.username);
        if (response.ok) {
          setSuccess('User deleted');
          navigate('/settings/users', { replace: true });
          return;
        }
        const detail = await parseApiErrorMessage(response);
        if (response.status === 403) {
          handleError(`You don't have permission to delete this user. ${detail}`);
        } else if (response.status === 404) {
          handleError(`User not found or already removed. ${detail}`);
        } else if (response.status === 409) {
          handleError(
            `This user can't be deleted right now (for example, linked records may need to be removed first). ${detail}`
          );
        } else {
          handleError(`Delete failed: ${detail}`);
        }
      } finally {
        setIsDeleting(false);
      }
    },
    [user.username, user.email, getConfirmation, handleError, setSuccess, navigate]
  );

  return (
    <Loader loading={user === undefined}>
      <form
        className="page"
        id="edit-user"
        onReset={handleReset}
        onSubmit={handleSubmit}
        ref={formRef}
        data-cy="edit-user-form"
      >
        {!!user && (
          <>
            <div className="page-header">
              <h1>USER {user?.username ?? 'N/A'}</h1>
              <div className="flex-grow-1 d-flex">
                {!user.active && (
                  <div className="font-weight-bold px-3 py-2 text-white" id="inactive-badge">
                    INACTIVE
                  </div>
                )}
              </div>
              <div className="d-flex g-3">
                <button className="btn-outline" type="reset">
                  CANCEL
                </button>
                {user.username !== userProfile.username && (
                  <Loader loading={isDeleting}>
                    <button
                      className="btn-danger"
                      type="button"
                      onClick={user.active ? handleDeactivate : handleActivate}
                      data-cy="edit-user-active-button"
                    >
                      {user.active ? 'DEACTIVATE' : 'ACTIVATE'}
                    </button>
                  </Loader>
                )}
                {user.username !== userProfile.username &&
                  [Role.ADMIN, Role.BILLING_ADMIN].includes(userProfile.role) && (
                    <Loader loading={isDeleting}>
                      <button
                        className="btn-danger"
                        type="button"
                        onClick={handleDeleteUser}
                        data-cy="edit-user-delete-button"
                      >
                        DELETE USER
                      </button>
                    </Loader>
                  )}
                <Loader loading={isSubmitting}>
                  <button className="btn-primary" type="submit">
                    SUBMIT
                  </button>
                </Loader>
              </div>
            </div>
            <div className="page-content">
              <div className="card-primary">
                <div className="card-header px-3">
                  <h4>PROFILE INFORMATION</h4>
                </div>
                <div className="card-body">
                  <div className="grid-4 p-2">
                    <div className="position-relative">
                      <h6 className="text-info-dark">USERNAME</h6>
                      <p className="text-black">{user.username}</p>
                    </div>
                    <div>
                      <h6 className="text-info-dark">EMAIL</h6>
                      <p className="text-black">{user.email}</p>
                    </div>
                    <div>
                      <h6 className="text-info-dark">ROLE</h6>
                      {[Role.ADMIN, Role.BILLING_ADMIN].includes(userProfile.role) && userProfile.username !== user.username ? (
                        <select
                          className="w-100 mt-2"
                          value={user.role ?? Role.NONE}
                          onChange={e => setUser({ ...user, role: e.target.value as Role })}
                          data-cy="edit-user-role-select"
                        >
                          <option value={Role.NONE} disabled hidden></option>
                          <option value={Role.ADMIN}>ADMIN</option>
                          <option value={Role.BILLING_ADMIN}>BILLING ADMIN</option>
                          <option value={Role.SENIOR_BOOKING}>BOOKING - SENIOR</option>
                          <option value={Role.BOOKING}>BOOKING</option>
                          <option value={Role.CARRIER}>CARRIER</option>
                          <option value={Role.ADMIN_CUSTOMER}>ADMIN CUSTOMER</option>
                          <option value={Role.CUSTOMER}>CUSTOMER</option>
                          <option value={Role.EXTERNAL}>EXTERNAL</option>
                          <option value={Role.GATE}>GATE</option>
                          <option value={Role.GATE_BOOKING}>GATE - BOOKING</option>
                        </select>
                      ) : (
                        <p className="text-black">{user.role}</p>
                      )}
                    </div>
                    <div>
                      <h6 className="text-info-dark">DEFAULT LOCATION</h6>
                      <div className="w-100 mt-2">
                        <SearchableSelect
                          required
                          options={
                            locations.size > 0
                              ? [...locations.values()].map(({ id, name }) => ({ label: name, value: `${id}` }))
                              : [...allLocations.values()].map(({ id, name }) => ({ label: name, value: `${id}` }))
                          }
                          onValueSelected={id =>
                            locations.size > 0
                              ? setUser({ ...user, defaultLocation: locations.get(Number(id)) })
                              : setUser({ ...user, defaultLocation: allLocations.get(Number(id)) })
                          }
                          value={`${defaultLocation?.id ?? ''}`}
                          data-cy="edit-user-default-location-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid-auto">
                <UserCustomers
                  user={user}
                  role={role}
                  customers={customers}
                  allCustomers={allCustomers}
                  setUser={setUser}
                  disabled={![Role.ADMIN, Role.BILLING_ADMIN].includes(userProfile.role)}
                />
                <UserLocations
                  user={user}
                  role={role}
                  locations={locations}
                  allLocations={allLocations}
                  setUser={setUser}
                  disabled={![Role.ADMIN, Role.BILLING_ADMIN].includes(userProfile.role)}
                />
                <UserTruckingCompanies
                  user={user}
                  role={role}
                  setUser={setUser}
                  disabled={![Role.ADMIN, Role.BILLING_ADMIN].includes(userProfile.role)}
                />
              </div>
            </div>
          </>
        )}
      </form>
      <ConfirmationDialog danger />
      <ErrorDialog />
    </Loader>
  );
};
